import { createSlice } from "@reduxjs/toolkit"
import type { PayloadAction } from "@reduxjs/toolkit"
import type { BillingPreviewUI } from "@/domain/billing/billing-preview.model"

/**
 * Final billing snapshot used after `orderCheckout` / `API_PATHS.ORDER_CHECKOUT` (and order GET for retries).
 * Separate from {@link BillingPreviewUI}; this is the payable snapshot for payment.
 */
export interface FinalBillingSummary {
  billing_log_id: string
  version_number: number
  is_current: boolean
  sub_total: number
  total_discount: number
  total_tax: number
  total_fees: number
  payable_amount: number
}

export interface FinalBillingResponse {
  order_id: string
  summary: FinalBillingSummary
  appliedDiscountList: unknown[]
  appliedChargesList: unknown[]
  is_scuccess?: boolean
  is_success?: boolean
}

export interface FinalBillingState {
  /** Latest authoritative totals before payment (from order-checkout or order GET). */
  lastFinalBilling: FinalBillingResponse | null
  /** Mirrors {@link lastFinalBilling.order_id} while awaiting payment / failure UX. Cleared when billing clears. */
  pendingOrderId: string | null
  /**
   * Set immediately before redirecting to the payment gateway (for global postMessage handling).
   */
  awaitingPaymentOrderId: string | null
  /** Last payment_attempt_id issued for the active gateway session (debug / reconciliation). */
  awaitingPaymentAttemptId: string | null
  /**
   * Billing preview from backend API (`previewOrder` → `API_PATHS.BILLING_PREVIEW`).
   * Fetched before order creation to show user final amount.
   * Cleared after order creation or on checkout reset.
   */
  billingPreview: BillingPreviewUI | null
}

const initialState: FinalBillingState = {
  lastFinalBilling: null,
  pendingOrderId: null,
  awaitingPaymentOrderId: null,
  awaitingPaymentAttemptId: null,
  billingPreview: null,
}

export const finalBillingSlice = createSlice({
  name: "finalBilling",
  initialState,
  reducers: {
    setFinalBilling: (state, action: PayloadAction<FinalBillingResponse | null>) => {
      state.lastFinalBilling = action.payload
      if (action.payload) state.pendingOrderId = null
    },
    clearFinalBilling: (state) => {
      state.lastFinalBilling = null
      state.pendingOrderId = null
      state.awaitingPaymentOrderId = null
      state.awaitingPaymentAttemptId = null
      state.billingPreview = null
    },
    setPendingOrderId: (state, action: PayloadAction<string | null>) => {
      state.pendingOrderId = action.payload
    },
    setAwaitingPaymentOrderId: (state, action: PayloadAction<string | null>) => {
      state.awaitingPaymentOrderId = action.payload
    },
    setAwaitingPaymentAttemptId: (state, action: PayloadAction<string | null>) => {
      state.awaitingPaymentAttemptId = action.payload
    },
    setBillingPreview: (state, action: PayloadAction<BillingPreviewUI | null>) => {
      state.billingPreview = action.payload
    },
  },
})

export const {
  setFinalBilling,
  clearFinalBilling,
  setPendingOrderId,
  setAwaitingPaymentOrderId,
  setAwaitingPaymentAttemptId,
  setBillingPreview,
} = finalBillingSlice.actions
export default finalBillingSlice.reducer
